import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  CreditCard, 
  Users, 
  CheckCircle, 
  Phone, 
  Mail, 
  ExternalLink,
  Loader2,
  Shield,
  TrendingDown,
  FileText,
  Handshake
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreditCounsellingPage = () => {
  const DEFAULT_REFERRER = {
    referrerName: 'Utmost Financial Inc.',
    referrerEmail: 'hello@utmostfinancial.ca',
    referrerPhone: '1-877-239-9060',
  };
  const [formData, setFormData] = useState({
    ...DEFAULT_REFERRER,
    clientFirstName: '',
    clientLastName: '',
    clientEmail: '',
    clientPhone: '',
    clientProvince: '',
    totalDebt: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${API_URL}/api/referrals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          referrer_name: formData.referrerName,
          referrer_email: formData.referrerEmail,
          referrer_phone: formData.referrerPhone,
          client_first_name: formData.clientFirstName,
          client_last_name: formData.clientLastName,
          client_email: formData.clientEmail,
          client_phone: formData.clientPhone,
          client_province: formData.clientProvince,
          total_debt: formData.totalDebt || null,
          notes: formData.notes || null
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          ...DEFAULT_REFERRER,
          clientFirstName: '',
          clientLastName: '',
          clientEmail: '',
          clientPhone: '',
          clientProvince: '',
          totalDebt: '',
          notes: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const provinces = [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick',
    'Newfoundland and Labrador', 'Nova Scotia', 'Ontario',
    'Prince Edward Island', 'Quebec', 'Saskatchewan',
    'Northwest Territories', 'Nunavut', 'Yukon'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden bg-[#0077B6]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0077B6] to-[#005A8C]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">Debt Management Services</h1>
          <p className="text-lg text-blue-100 mb-6">
            Partner with Credit Lift to help clients eliminate debt and rebuild their financial future
          </p>
          <Button
            asChild
            size="lg"
            className="bg-[#D4AF37] text-[#003D5C] hover:bg-[#B8962E] font-bold"
          >
            <a href="#referral-form">
              Submit a Referral
            </a>
          </Button>
        </div>
      </section>

      {/* About Credit Lift Section */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">About Credit Lift</h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              Utmost Financial Inc. is proudly partnered with Credit Lift — a trusted Canadian debt
              management provider that helps individuals overcome financial challenges through
              proven debt elimination strategies.
            </p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <Card className="bg-[#F0F7FF] border border-blue-100 text-center">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mx-auto mb-4">
                  <TrendingDown className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Debt Elimination</h3>
                <p className="text-gray-600 text-sm">Proven strategies to reduce and eliminate debt</p>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 text-center">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Credit Protection</h3>
                <p className="text-gray-600 text-sm">Protect and rebuild your credit score</p>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 text-center">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Free Consultation</h3>
                <p className="text-gray-600 text-sm">No-obligation assessment of your situation</p>
              </CardContent>
            </Card>

            <Card className="bg-[#F0F7FF] border border-blue-100 text-center">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mx-auto mb-4">
                  <Handshake className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Personalized Plans</h3>
                <p className="text-gray-600 text-sm">Customized solutions for your needs</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white font-bold"
            >
              <a href="https://creditlift.ca/utmost/" target="_blank" rel="noopener noreferrer">
                Learn More About Credit Lift <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-12 bg-[#F0F7FF]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">How the Referral Process Works</h2>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#0077B6] text-white flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Submit Referral</h3>
              <p className="text-gray-600">
                Fill out the referral form below with your client&apos;s information.
                Make sure to speak with them first so they&apos;re expecting a call.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#0077B6] text-white flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Credit Lift Contacts</h3>
              <p className="text-gray-600">
                The Credit Lift team will reach out to your referral to schedule
                a free consultation and assess their financial situation.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#0077B6] text-white flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Client Gets Help</h3>
              <p className="text-gray-600">
                Your client receives personalized debt relief guidance and 
                a clear path to financial freedom.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Referral Form Section */}
      <section className="py-12 bg-white" id="referral-form">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">Submit a Referral</h2>
            <p className="text-gray-600">
              Please ensure you have spoken to your referral before submitting this form. 
              We are currently accepting referrals for all of Canada.
            </p>
            <div className="h-1 w-24 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>

          <Card className="shadow-lg border border-gray-200">
            <CardContent className="p-8">
              {submitStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-10 w-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">Referral Submitted!</h3>
                  <p className="text-gray-600 mb-6">
                    Thank you for your referral. Our team will contact your client within 1-2 business days.
                  </p>
                  <Button 
                    onClick={() => setSubmitStatus(null)}
                    className="bg-[#0077B6] hover:bg-[#005A8C]"
                  >
                    Submit Another Referral
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                      Something went wrong. Please try again or contact us at hello@utmostfinancial.ca
                    </div>
                  )}

                  {/* Referrer Information */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                      <Users className="h-5 w-5 mr-2 text-[#0077B6]" />
                      Your Information (Referrer)
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Your Name *
                        </label>
                        <input
                          type="text"
                          name="referrerName"
                          value={formData.referrerName}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Your Email *
                        </label>
                        <input
                          type="email"
                          name="referrerEmail"
                          value={formData.referrerEmail}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                          placeholder="your.email@example.com"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Your Phone *
                        </label>
                        <input
                          type="tel"
                          name="referrerPhone"
                          value={formData.referrerPhone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                          placeholder="(204) 555-0123"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Client Information */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                      <CreditCard className="h-5 w-5 mr-2 text-[#D4AF37]" />
                      Client Information (Person Being Referred)
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Client First Name *
                        </label>
                        <input
                          type="text"
                          name="clientFirstName"
                          value={formData.clientFirstName}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                          placeholder="First name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Client Last Name *
                        </label>
                        <input
                          type="text"
                          name="clientLastName"
                          value={formData.clientLastName}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                          placeholder="Last name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Client Email *
                        </label>
                        <input
                          type="email"
                          name="clientEmail"
                          value={formData.clientEmail}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                          placeholder="client.email@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Client Phone *
                        </label>
                        <input
                          type="tel"
                          name="clientPhone"
                          value={formData.clientPhone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                          placeholder="(204) 555-0123"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Province *
                        </label>
                        <select
                          name="clientProvince"
                          value={formData.clientProvince}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                        >
                          <option value="">Select province...</option>
                          {provinces.map(province => (
                            <option key={province} value={province}>{province}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Estimated Total Debt
                        </label>
                        <select
                          name="totalDebt"
                          value={formData.totalDebt}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                        >
                          <option value="">Select range...</option>
                          <option value="Under $10,000">Under $10,000</option>
                          <option value="$10,000 - $25,000">$10,000 - $25,000</option>
                          <option value="$25,000 - $50,000">$25,000 - $50,000</option>
                          <option value="$50,000 - $100,000">$50,000 - $100,000</option>
                          <option value="Over $100,000">Over $100,000</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Additional Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Notes (Optional)
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent resize-none"
                      placeholder="Any additional information about the client's situation..."
                    />
                  </div>

                  {/* Disclaimer */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800">
                      <strong>Important:</strong> By submitting this form, you confirm that you have spoken
                      to the client and they are expecting to be contacted by Credit Lift regarding
                      debt management services.
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-[#003D5C] font-bold py-4 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Submitting Referral...
                      </>
                    ) : (
                      'Submit Referral'
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 bg-[#0077B6]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Questions About Debt Relief?</h2>
          <p className="text-blue-100 text-lg mb-6">
            Contact us to learn more about how we can help your clients achieve financial freedom.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#003D5C] font-bold"
            >
              <a href="mailto:hello@utmostfinancial.ca">
                <Mail className="h-5 w-5 mr-2" />
                hello@utmostfinancial.ca
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10 font-bold"
            >
              <a href="tel:18772399060">
                <Phone className="h-5 w-5 mr-2" />
                1-877-239-9060
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CreditCounsellingPage;
